import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { LuCircleCheck } from 'react-icons/lu';
import { useColorContext } from '../context/colorContext';
import { useUserContext } from '../context/userContext';
import { useLanguageContext } from '../context/languageContext';
import { getCachedStripePlans, loadStripePlans } from '../services/stripePlansCache';

const BACKEND_BASE_URL = String(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001').trim().replace(/\/$/, '');

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ORDERED_KEYS = ['unlimited_monthly', 'unlimited_yearly'];

export default function PayementOptionPopup({ isOpen, onClose, currentPlanKey, limitMessage }) {
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, theme_type } = useColorContext();
  const { user_id, email, name, is_authenticated } = useUserContext();
  const { t } = useLanguageContext();

  const [stripePlans, setStripePlans] = useState(() => getCachedStripePlans());
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState('');
  const [checkoutLoadingKey, setCheckoutLoadingKey] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');

  const isLightTheme = String(theme_type || '').toLowerCase() === 'clear';
  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const errorTextColor = isLightTheme ? '#000000' : '#FCA5A5';
  const safeAccent = toSafeHex(activeAccent, '#6366F1');

  useEffect(() => {
    if (!isOpen) return;
    setCheckoutError('');
    const hydrate = async () => {
      const cached = getCachedStripePlans();
      if (cached.length > 0) { setStripePlans(cached); return; }
      setLoadingPlans(true);
      try {
        const plans = await loadStripePlans();
        setStripePlans(Array.isArray(plans) ? plans : []);
      } catch (err) {
        setPlansError(err.message || 'Unable to load Stripe plans.');
      } finally {
        setLoadingPlans(false);
      }
    };
    hydrate();
  }, [isOpen]);

  const planDisplay = useMemo(() => ({
    unlimited_monthly: {
      name: t('payment.monthly'),
      features: [t('payment.featureUnlimitedFormats'), t('payment.featureUnlimitedChat'), t('payment.featureUnlimitedUploads'), t('payment.featureEmailSupport')],
      popular: false,
      cta: t('payment.chooseMonthly'),
    },
    unlimited_yearly: {
      name: t('payment.yearly'),
      features: [t('payment.featureUnlimitedFormats'), t('payment.featureUnlimitedChat'), t('payment.featureUnlimitedUploads'), t('payment.featureEmailSupport')],
      popular: true,
      cta: t('payment.chooseYearly'),
    },
  }), [t]);

  const plans = useMemo(() => {
    const byKey = new Map(
      stripePlans
        .filter((p) => p && ORDERED_KEYS.includes(String(p.planKey || '').toLowerCase()))
        .map((p) => [String(p.planKey || '').toLowerCase(), p])
    );
    const fmt = (amount, currency) => {
      try {
        const formatted = new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
        return formatted.replace('$US', '$').replace('US$', '$');
      } catch (_) {
        return `${amount.toFixed(2)} ${currency}`;
      }
    };
    const monthlyAmount = byKey.has('unlimited_monthly')
      ? (typeof byKey.get('unlimited_monthly').amount === 'number' ? byKey.get('unlimited_monthly').amount : 0) / 100
      : null;
    const yearlyAmount = byKey.has('unlimited_yearly')
      ? (typeof byKey.get('unlimited_yearly').amount === 'number' ? byKey.get('unlimited_yearly').amount : 0) / 100
      : null;
    const savingsPct = (monthlyAmount && yearlyAmount && monthlyAmount > 0)
      ? Math.round((1 - yearlyAmount / (monthlyAmount * 12)) * 100)
      : null;

    return ORDERED_KEYS.filter((k) => byKey.has(k)).map((k) => {
      const sp = byKey.get(k);
      const preset = planDisplay[k];
      const interval = String(sp.interval || '').toLowerCase();
      const amount = typeof sp.amount === 'number' ? sp.amount : 0;
      const currency = String(sp.currency || 'eur').toUpperCase();
      const amountInCurrency = amount / 100;
      const weeklyAmount = interval === 'year' ? amountInCurrency / 52 : amountInCurrency / 4.33;
      const weeklyPrice = fmt(weeklyAmount, currency);
      const billedTotal = fmt(amountInCurrency, currency);
      const billedLabel = interval === 'year'
        ? t('payment.billedYearly', { amount: billedTotal })
        : t('payment.billedMonthly', { amount: billedTotal });
      const currentKey = String(currentPlanKey || '').toLowerCase();
      const isCurrentPlan = currentKey === k ||
        (currentKey === 'unlimited' && (k === 'unlimited_monthly' || k === 'unlimited_yearly'));
      return {
        id: `stripe-${k}`,
        planKey: k,
        name: preset.name,
        weeklyPrice,
        billedLabel,
        savingsPct: k === 'unlimited_yearly' ? savingsPct : null,
        features: preset.features,
        popular: preset.popular,
        cta: preset.cta,
        isCurrentPlan,
      };
    });
  }, [stripePlans, currentPlanKey, planDisplay, t]);

  const handleSelect = async (plan) => {
    if (!is_authenticated) {
      setCheckoutError('You must be signed in to subscribe.');
      return;
    }
    if (plan.isCurrentPlan || checkoutLoadingKey !== null) return;
    setCheckoutError('');
    setCheckoutLoadingKey(plan.planKey);
    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/payment/create-subscription-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planKey: plan.planKey, userId: user_id, customerEmail: email, customerName: name }),
      });
      const raw = await res.text();
      let data;
      try { data = JSON.parse(raw); } catch (_) { throw new Error('Invalid server response.'); }
      if (!res.ok) throw new Error(data.error || 'Unable to launch checkout.');
      if (!data.checkoutUrl) throw new Error('Checkout URL missing.');
      window.history.replaceState({}, '', '/settings');
      window.location.assign(data.checkoutUrl);
    } catch (err) {
      setCheckoutError(err.message || 'Unknown error.');
      setCheckoutLoadingKey(null);
    }
  };

  if (!isOpen) return null;

  const portalTarget = typeof document !== 'undefined'
    ? (document.getElementById('root') || document.body)
    : null;

  if (!portalTarget) return null;

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(0,0,0,0.62)',
        backdropFilter: 'blur(10px) saturate(112%)',
        animation: 'upgPopBackdropIn 180ms ease both',
      }}
    >
      <style>{`
        @keyframes upgPopBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes upgPopSlideIn { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .upgpop-card:not(.is-current):hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 38px rgba(0,0,0,0.26);
          border-color: ${hexToRgba(safeAccent, 0.42, safeAccent)} !important;
        }
        .upgpop-cta:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px ${hexToRgba(safeAccent, 0.24, safeAccent)};
        }
      `}</style>

      <div
        style={{
          width: 'min(980px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 24,
          border: `1px solid ${hexToRgba(safeTextSecondary, 0.2, safeTextSecondary)}`,
          background: `linear-gradient(160deg, ${hexToRgba(safeBgSecondary, 0.97, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 100%)`,
          boxShadow: `0 32px 80px rgba(0,0,0,0.54), inset 0 1px 0 ${hexToRgba(safeTextPrimary, 0.06, safeTextPrimary)}`,
          padding: '28px 24px 32px',
          animation: 'upgPopSlideIn 220ms ease both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, color: safeTextPrimary, fontSize: '1.4rem', fontWeight: 760, letterSpacing: '-0.01em' }}>
              {t('payment.chooseSubscription')}
            </h2>
            <p style={{ margin: '5px 0 0', color: safeTextSecondary, fontSize: '0.9rem' }}>
              {t('payment.selectPlan')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 36, height: 36, flexShrink: 0,
              borderRadius: 10,
              border: `1px solid ${hexToRgba(safeTextSecondary, 0.22, safeTextSecondary)}`,
              background: hexToRgba(safeTextPrimary, 0.06, safeTextPrimary),
              color: safeTextSecondary,
              display: 'grid', placeItems: 'center',
              cursor: 'pointer',
              transition: 'background 140ms ease',
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {limitMessage ? (
          <div style={{
            marginBottom: 16, borderRadius: 12,
            border: `1px solid ${hexToRgba(safeAccent, 0.45, safeAccent)}`,
            background: hexToRgba(safeAccent, 0.12, safeAccent),
            color: safeTextPrimary, padding: '12px 14px', fontSize: '0.9rem', lineHeight: 1.5,
          }}>
            {limitMessage}
          </div>
        ) : null}

        {!is_authenticated && (
          <div style={{
            marginBottom: 16, borderRadius: 12,
            border: '1px solid rgba(239,68,68,0.42)', background: 'rgba(239,68,68,0.12)',
            color: errorTextColor, padding: '10px 14px', fontSize: '0.9rem',
          }}>
            {t('payment.mustBeSignedIn')}
          </div>
        )}

        {checkoutError && (
          <div style={{
            marginBottom: 16, borderRadius: 12,
            border: '1px solid rgba(239,68,68,0.42)', background: 'rgba(239,68,68,0.12)',
            color: errorTextColor, padding: '10px 14px', fontSize: '0.9rem',
          }}>
            {checkoutError}
          </div>
        )}

        {plansError && (
          <div style={{
            marginBottom: 16, borderRadius: 12,
            border: '1px solid rgba(239,68,68,0.42)', background: 'rgba(239,68,68,0.12)',
            color: errorTextColor, padding: '10px 14px', fontSize: '0.9rem',
          }}>
            {plansError}
          </div>
        )}

        {loadingPlans ? (
          <div style={{ color: safeTextSecondary, fontSize: '0.9rem', textAlign: 'center', padding: '40px 0' }}>
            {t('payment.loadingPlans')}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 18,
          }}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`upgpop-card${plan.isCurrentPlan ? ' is-current' : ''}`}
                style={{
                  padding: '26px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.82, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.62, safeBgPrimary)} 100%)`,
                  border: plan.popular
                    ? `2px solid ${safeAccent}`
                    : `1px solid ${hexToRgba(safeAccent, 0.14, safeAccent)}`,
                  borderRadius: 20,
                  position: 'relative',
                  transition: 'transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease',
                  opacity: plan.isCurrentPlan ? 0.68 : 1,
                }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    padding: '4px 14px', background: safeAccent, borderRadius: 40,
                    fontSize: '0.72rem', fontWeight: 600, color: '#FFFFFF', whiteSpace: 'nowrap',
                  }}>
                    {t('payment.mostPopular')}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, color: safeTextPrimary, fontSize: '1.18rem', fontWeight: 700 }}>
                    {plan.name}
                  </h3>
                  {plan.savingsPct > 0 && (
                    <span style={{
                      padding: '3px 9px',
                      borderRadius: 20,
                      background: '#16a34a',
                      color: '#ffffff',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.01em',
                      whiteSpace: 'nowrap',
                    }}>
                      {t('payment.save', { pct: plan.savingsPct })}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: 18, marginBottom: 2 }}>
                  <div style={{ fontSize: '2.4rem', fontWeight: 800, color: safeTextPrimary, lineHeight: 1.1 }}>
                    {plan.weeklyPrice}
                    <span style={{ fontSize: '0.88rem', color: safeTextSecondary, fontWeight: 400, marginLeft: 4 }}>
                      {t('payment.perWeek')}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.76rem', color: safeTextSecondary, marginTop: 4 }}>
                    {plan.billedLabel}
                  </div>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ display: 'flex', alignItems: 'center', gap: 10, color: safeTextSecondary, fontSize: '0.87rem' }}>
                      <LuCircleCheck size={15} color={safeAccent} style={{ flexShrink: 0 }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className="upgpop-cta"
                  disabled={plan.isCurrentPlan || !is_authenticated || checkoutLoadingKey !== null}
                  onClick={() => handleSelect(plan)}
                  style={{
                    width: '100%',
                    marginTop: 'auto',
                    padding: '12px',
                    borderRadius: 50,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: (plan.isCurrentPlan || !is_authenticated || checkoutLoadingKey !== null) ? 'not-allowed' : 'pointer',
                    border: plan.popular ? 'none' : `1px solid ${hexToRgba(safeAccent, 0.24, safeAccent)}`,
                    color: plan.isCurrentPlan ? safeTextSecondary : (plan.popular ? '#FFFFFF' : safeTextPrimary),
                    background: plan.isCurrentPlan
                      ? hexToRgba(safeTextSecondary, 0.14, safeTextSecondary)
                      : plan.popular
                        ? `linear-gradient(135deg, ${safeAccent} 0%, ${hexToRgba(safeAccent, 0.72, safeAccent)} 100%)`
                        : hexToRgba(safeBgSecondary, 0.8, safeBgSecondary),
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    opacity: (checkoutLoadingKey !== null && checkoutLoadingKey !== plan.planKey) ? 0.55 : 1,
                  }}
                >
                  {plan.isCurrentPlan
                    ? t('payment.currentPlan')
                    : checkoutLoadingKey === plan.planKey
                      ? t('payment.redirecting')
                      : plan.cta}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    portalTarget
  );
}
